/**
 * Engagement ingest — the one write in this client that is not a mutation.
 *
 * A plain function rather than a hook, deliberately. Every other write here is
 * a `useMutation` because the screen cares whether it succeeded; this one is
 * fire-and-forget telemetry whose failure the learner must never see, and
 * whose caller is a timer rather than a component. Wrapping it in query state
 * would invite a retry policy, an error boundary and a loading flag, none of
 * which belong anywhere near a heartbeat.
 *
 * It is also why this does not invalidate anything: nothing on any learner
 * screen renders from the event stream. The reports that do are on a different
 * surface, behind a different identity.
 */
import type { EventIngestBody, EventIngestResponse } from '@cusp/contracts';
import type { Identity } from './identity.js';
import { request } from './request.js';

export function postEvents(
  body: EventIngestBody,
  options: { keepalive?: boolean; identity?: Identity } = {},
): Promise<EventIngestResponse> {
  return request<EventIngestResponse>('/v1/me/events', {
    method: 'POST',
    body,
    ...(options.keepalive === undefined ? {} : { keepalive: options.keepalive }),
    /*
     * PINNED, not current. A visit's queue is flushed when the identity that
     * produced it is on its way out — a membership switch retires the old
     * beacon — and by then `authHeaders()` names the NEW institution. Those
     * events would be filed against a tenant they did not happen in.
     */
    ...(options.identity === undefined ? {} : { identity: options.identity }),
  });
}
